/**
 * Search and filter utilities for counter orders
 * Pure functions - no dependencies on component state
 */
class SearchUtils {
  /**
   * Trim leading and trailing spaces, backslashes, and forward slashes.
   * Use for counter search input normalization.
   * @param {string} value - Raw input
   * @returns {string} Trimmed value (or "" if null/undefined/non-string)
   */
  static trimLeadingTrailingSlashesAndSpaces(value) {
    if (value === null || value === undefined) {
      return "";
    }
    const str = typeof value === "string" ? value : String(value);
    return str.replace(/^[\s\\/]+|[\s\\/]+$/g, "");
  }

  /** Default trim characters when location trim_chars is empty: backslash and space (comma-separated form) */
  static get defaultTrimChars() {
    return " , \\";
  }

  /**
   * Parse comma-separated trim_chars into a single string of characters to trim.
   * @param {string} trimChars - Comma-separated list (e.g. " , \\, /"). Empty uses default.
   * @returns {string} Flat string of characters for regex character class
   */
  static parseTrimChars(trimChars) {
    if (typeof trimChars !== "string" || trimChars.length === 0) {
      return SearchUtils.defaultTrimChars;
    }
    return trimChars.split(",").map((s) => s.trim()).join("");
  }

  /**
   * Trim leading and trailing characters from a set (e.g. location trim_chars, comma-separated).
   * Escapes regex metacharacters so input is treated literally.
   * @param {string} value - Raw input
   * @param {string} trimChars - Comma-separated characters to trim (e.g. " , \\, /"). Empty uses default.
   * @returns {string} Trimmed value (or "" if null/undefined/non-string)
   */
  static trimLeadingTrailing(value, trimChars) {
    if (value === null || value === undefined) {
      return "";
    }
    const str = typeof value === "string" ? value : String(value);
    const chars = SearchUtils.parseTrimChars(trimChars);
    const escaped = chars.split("").map((c) => (/[\]\\^-]/.test(c) ? "\\" + c : c)).join("");
    const re = new RegExp(`^[${escaped}]+|[${escaped}]+$`, "g");
    return str.replace(re, "");
  }

  /**
   * Normalize search value to lowercase trimmed string
   */
  static normalizeSearchValue(value) {
    if (value === null || value === undefined) {
      return "";
    }
    return value.toString().trim().toLowerCase();
  }

  /**
   * Extract the raw pickup code from a location-prefixed QR value.
   * Examples: "Counter-N522EP" -> "N522EP", "Bedside:N522EP" -> "N522EP".
   */
  static extractPickupCodeFromShortcut(value) {
    if (value === null || value === undefined) {
      return "";
    }
    const str = typeof value === "string" ? value : String(value);
    const match = str.trim().match(/^(counter|bedside)\s*[-:]\s*(.+)$/i);
    return match && match[2] ? match[2].trim() : "";
  }

  static normalizePickupSearchValue(value) {
    const shortcutPickupCode = SearchUtils.extractPickupCodeFromShortcut(value);
    return SearchUtils.normalizeSearchValue(shortcutPickupCode || value);
  }

  /**
   * Extract search tokens from a value
   */
  static getSearchTokens(value) {
    const normalizedValue = SearchUtils.normalizeSearchValue(value);
    if (!normalizedValue) {
      return [];
    }
    return normalizedValue.split(/\s+/).filter(Boolean);
  }

  /**
   * Get all possible name variations for an order
   */
  static getOrderNameCandidates(order) {
    if (!order) {
      return [];
    }

    const candidates = new Set();
    const firstName = SearchUtils.normalizeSearchValue(order.first_name);
    const lastName = SearchUtils.normalizeSearchValue(order.last_name);
    const customerName = SearchUtils.normalizeSearchValue(order.customerName);
    const nameField = SearchUtils.normalizeSearchValue(order.name);
    const patientName = SearchUtils.normalizeSearchValue(
      order.patient_name || order.patientName
    );

    const collapseWhitespace = (value) =>
      value ? value.replace(/\s+/g, " ").trim() : "";

    const combinedName = collapseWhitespace(
      `${firstName || ""} ${lastName || ""}`.trim()
    );
    const reversedName = collapseWhitespace(
      `${lastName || ""}, ${firstName || ""}`.trim()
    );

    [
      firstName,
      lastName,
      customerName,
      nameField,
      patientName,
      combinedName,
      reversedName,
    ]
      .filter(Boolean)
      .forEach((candidate) => candidates.add(candidate));

    return Array.from(candidates);
  }

  /**
   * Extract digits from a value (for DOB matching)
   */
  static extractDobDigits(value) {
    if (value === null || value === undefined) {
      return "";
    }
    return value.toString().replace(/\D/g, "").slice(0, 8);
  }

  /**
   * Get all possible DOB digit variations for an order
   * @param {Object} order - Order object
   * @param {Function} normalizeDobValue - Function to normalize DOB (from DateUtils)
   */
  static getOrderDobDigits(order, normalizeDobValue) {
    if (!order) {
      return [];
    }

    const dobCandidates = new Set();
    const candidateValues = [
      order.dob_str,
      order.dob,
      order.dobStr,
      order.customerDob,
      order.patientDob,
    ];

    candidateValues.forEach((value) => {
      if (!value) {
        return;
      }

      if (normalizeDobValue) {
        const normalizedDob = normalizeDobValue(value);
        if (normalizedDob) {
          const normalizedDobDigits = SearchUtils.extractDobDigits(normalizedDob);
          if (normalizedDobDigits) {
            dobCandidates.add(normalizedDobDigits);
          }
        }
      }

      const digitsOnly = SearchUtils.extractDobDigits(value);
      if (digitsOnly) {
        dobCandidates.add(digitsOnly);
      }
    });

    return Array.from(dobCandidates).filter(Boolean);
  }

  /**
   * Check if an order matches the search criteria
   * @param {Object} order - Order to check
   * @param {string} normalizedSearch - Normalized search text
   * @param {Array} tokens - Search tokens
   * @param {string} searchDobDigits - DOB digits from search
   * @param {Function} normalizeDobValue - Function to normalize DOB (from DateUtils)
   */
  static doesOrderMatchSearch(
    order,
    normalizedSearch,
    tokens,
    searchDobDigits,
    normalizeDobValue
  ) {
    if (!normalizedSearch) {
      return false;
    }

    const normalizedPickupSearch = SearchUtils.normalizePickupSearchValue(normalizedSearch);
    const puCode = SearchUtils.normalizeSearchValue(order && order.pucode);
    if (puCode && puCode === normalizedPickupSearch) {
      return true;
    }

    if (searchDobDigits && searchDobDigits.length === 8) {
      const dobDigitsCandidates = SearchUtils.getOrderDobDigits(
        order,
        normalizeDobValue
      );
      if (
        dobDigitsCandidates.some((candidate) => {
          return candidate && candidate === searchDobDigits;
        })
      ) {
        return true;
      }
    }

    const activeTokens =
      tokens && tokens.length > 0
        ? tokens
        : SearchUtils.getSearchTokens(normalizedSearch);

    if (!activeTokens.length) {
      return false;
    }

    const candidates = SearchUtils.getOrderNameCandidates(order);
    if (!candidates.length) {
      return false;
    }

    return activeTokens.every((token) =>
      candidates.some((candidate) => candidate.includes(token))
    );
  }

  /**
   * Find all orders matching the search text
   * @param {string} searchText - Search text
   * @param {Array} collections - Array of {type, data} objects
   * @param {Function} normalizeDobValue - Function to normalize DOB (from DateUtils)
   */
  static findOrdersMatchingSearch(searchText, collections = [], normalizeDobValue) {
    const normalizedSearch = SearchUtils.normalizeSearchValue(searchText);
    if (!normalizedSearch) {
      return [];
    }

    const tokens = SearchUtils.getSearchTokens(searchText);
    const extractedDobDigits = SearchUtils.extractDobDigits(searchText);
    const searchDobDigits =
      extractedDobDigits && extractedDobDigits.length === 8
        ? extractedDobDigits
        : "";
    const matches = [];
    const seenKeys = new Set();

    collections.forEach(({ type, data }) => {
      const results = (data && data.results) || [];
      results.forEach((order) => {
        if (!order) {
          return;
        }
        if (
          !SearchUtils.doesOrderMatchSearch(
            order,
            normalizedSearch,
            tokens,
            searchDobDigits,
            normalizeDobValue
          )
        ) {
          return;
        }

        const uniqueKey =
          order.id ||
          `${type || "unknown"}-${order.pucode || ""}-${order.customer_id || ""}-${order.created_on || ""}`;

        if (seenKeys.has(uniqueKey)) {
          return;
        }

        seenKeys.add(uniqueKey);
        matches.push({ ...order, deliveryType: type });
      });
    });

    return matches;
  }

  /**
   * Find first exact match for Name or DOB in a list of orders
   * @param {string} searchText - Raw search text
   * @param {Array} orders - List of orders to search
   * @param {Function} normalizeDobValue - Function to normalize DOB (from DateUtils)
   */
  static findExactMatch(searchText, orders = [], normalizeDobValue) {
    const normalizedSearch = SearchUtils.normalizeSearchValue(searchText);
    if (!normalizedSearch) {
      return null;
    }

    const searchDobDigits = SearchUtils.extractDobDigits(searchText);
    const isSearchFullDob = searchDobDigits && searchDobDigits.length === 8;

    for (const order of orders) {
      if (!order) continue;

      if (isSearchFullDob) {
        const orderDobDigits = SearchUtils.getOrderDobDigits(order, normalizeDobValue);
        if (orderDobDigits.includes(searchDobDigits)) {
          return { order, matchType: "dob" };
        }
      }

      const candidates = SearchUtils.getOrderNameCandidates(order);
      if (candidates.some((candidate) => candidate === normalizedSearch)) {
        return { order, matchType: "name" };
      }
    }

    return null;
  }
}

export default SearchUtils;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/utils/SearchUtils.js